import { Card, CardContent, LoadingState } from '@sitewise/ui';

import type { ActivityEntry } from '../../../api/buildings.js';

interface Props {
  entries: ActivityEntry[];
  isLoading: boolean;
}

/**
 * Per-unit activity feed. Pulls from the audit log so every mutation
 * — direct (PATCH /units/:id) and indirect (work order created with
 * unit_id, document uploaded, custom field value changed) — shows up
 * in chronological order.
 */
export function ActivityTab({ entries, isLoading }: Props) {
  if (isLoading) return <LoadingState rows={4} />;
  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-fg-secondary">
          No activity recorded for this unit yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="divide-y divide-border p-0 text-sm">
        {entries.map((entry) => (
          <div key={entry.id} className="flex items-start justify-between gap-3 px-5 py-3">
            <div>
              <div className="font-medium text-fg">{prettyAction(entry.action)}</div>
              <div className="text-xs text-fg-muted">
                {entry.actorKind === 'super_admin' ? 'super-admin' : 'user'} ·{' '}
                {new Date(entry.createdAt).toLocaleString()}
              </div>
              {renderMetadataLine(entry.metadata)}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function prettyAction(action: string): string {
  switch (action) {
    case 'unit.created':
      return 'Unit added';
    case 'unit.updated':
      return 'Unit updated';
    case 'unit.archived':
      return 'Unit archived';
    case 'unit.document_added':
      return 'Document uploaded';
    case 'unit.document_updated':
      return 'Document updated';
    case 'unit.document_deleted':
      return 'Document archived';
    case 'unit_field_value.set':
      return 'Custom field set';
    case 'unit_field_value.cleared':
      return 'Custom field cleared';
    case 'work_order.created':
      return 'Work order created';
    case 'work_order.completed':
      return 'Work order completed';
    case 'inspection.created':
      return 'Inspection created';
    case 'inspection.submitted':
      return 'Inspection submitted';
    case 'compliance.log_created':
      return 'Compliance log filed';
    default:
      return action;
  }
}

function renderMetadataLine(metadata: unknown): React.ReactNode {
  if (!metadata || typeof metadata !== 'object') return null;
  const meta = metadata as Record<string, unknown>;
  const fields = Array.isArray(meta.fields) ? (meta.fields as string[]) : null;
  if (fields && fields.length > 0) {
    return (
      <div className="text-xs text-fg-muted">Changed: {fields.join(', ')}</div>
    );
  }
  if (typeof meta.filename === 'string') {
    return <div className="text-xs text-fg-muted">{meta.filename}</div>;
  }
  if (typeof meta.key === 'string') {
    return <div className="text-xs text-fg-muted">Field “{meta.key}”</div>;
  }
  return null;
}
